import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Colors from '../../../ui/global/Colors';
import Fonts from '../../../ui/global/Fonts';
import Button from '../../../ui/components/Button';
import GameTopBar from '../components/GameTopBar';
import GamePreview from '../components/GamePreview';
import { GithubFormat } from '../utils/formatCode';
import GameHistory from '../components/input/GameHistory';
import { GameModes } from '../Game';
import GamePlan from '../components/GamePlan';
import GameMessage from '../components/ai-output/GameMessage';
import StreamStateProvider from '../components/stream/helpers/StreamStateProvider';
import GameRuntimeError from '../components/ai-output/GameRuntimeError';
import GlobalMessage from '../../pricing/GlobalMessage';
import GameSettingsPanel from '../components/GameSettingsPanel';
import { DeviceType } from '../components/GamePreview';
import GameAssetsPanel from '../components/assets/GameAssetsPanel';
import example from '../utils/exampleGameArt.json';
import GameInput from '../components/input/GameInput';
import { GameHistoryEntry } from '../../../backend/useGame';
import { Helmet } from 'react-helmet-async';
import LoadingSpinner from '../../../ui/components/LoadingSpinner';
import { useMediaQuery } from '@mui/material';
import { type StreamContextType } from '../components/stream/helpers/Types';
import { SharedPanelProvider } from '../components/panels/SharedPanelState';

const Root = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: 100svh;
    background-color: ${Colors.black};
    color: ${Colors.white};
    overflow-y: auto;
    overflow-x: hidden;
    padding-top: 54px;
    gap: 10px;
`;

const Primary = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 100%;
    height: 100%;
`;

const Inner = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 100%;
    height: 100%;
    min-height: 0;
    padding: 5px 10px 10px 10px;
    gap: 10px;
`;

const StageContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 100%;
    flex: 1;
    min-height: 0;
    border-radius: 4px;
    // background-color: ${Colors.grey9};
    // border: 1px solid ${Colors.grey8};
    gap: 10px;
    overflow: hidden;
`;

// Legacy inline error alert removed (replaced by GameRuntimeError)

const PreviewLoading = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background-color: ${Colors.grey9};
    border: 1px solid ${Colors.grey8};
`;

interface GameEditProps {
    loading: boolean;
    config: any;
    gameId: string;
    gameName: string;
    mode: GameModes;
    setMode: (mode: GameModes) => void;
    history: Partial<GameHistoryEntry>[];
    selectedHistoryIndex: number;
    plan: string;
    code: GithubFormat;
    originalCode: GithubFormat;
    instruction: string;
    sandpackKey: string;
    iframeErrors: string[];
    // isStreaming and streamContent removed - will be reimplemented with SSE
    isGeneratingName: boolean;
    onHomeClick: () => void;
    onHistorySelect: (entry: Partial<GameHistoryEntry>) => void;
    onRefreshClick: () => void;
    handleGameNameChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleInstructionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    handleGenerateFromInput: (instruction: string) => void;
    handleIframeError: (error: string) => void;
    fixErrors: (errors: string[]) => void;
    onConstantChange?: (updatedCode: GithubFormat) => void;
    onSaveGameSettings?: (updatedCode: GithubFormat) => void;
    onSaveCodeEdit?: (updatedCode: GithubFormat) => void;
    onDeviceClick: () => void;
    devicePreview: DeviceType;
    onAIStatusChange: (isRunning: boolean) => void;
    isGeneratingGame: boolean;
    isUpdatingGameArt: boolean;
    onSubmitSuggested?: (selectedItems: { id: string; text: string; checked: boolean; interactive?: boolean }[]) => void;
    updatePlanStatus: (newStatus: string, newStatusType?: string, copyFromUserFacing?: boolean) => void;
    setIsUpdatingGameArt: (newStatus: boolean) => void;
    gameArtTriggerReload: number;
    incrementGameArtTriggerReload: () => void;
    currentGameHistoryId: string;
    gameCreateCompleted: boolean;
    loadingMode: boolean;
    errorsForModal: string[];
    handleTryToFix: () => void;
    handleUndoClick: () => void;
    canUndo: boolean;
    handleCloseErrorPopup: () => void;
    latestUserFacingHistoryId: string;
    latestUserFacingCode: GithubFormat;
    latestUserFacingPrompt: string;
    latestUserFacingStatus: string;
    onStreamContext?: (context: StreamContextType) => void;
    isViewingLatest: boolean;
    selectedEntrySnapshot: any[];
    realtimeArtEvent?: { row: any; type: 'INSERT'|'UPDATE'|'DELETE' };
}

const GameEdit: React.FC<GameEditProps> = ({
    loading,
    config,
    gameId,
    gameName,
    mode,
    setMode,
    history,
    selectedHistoryIndex,
    code,
    originalCode,
    plan,
    instruction,
    sandpackKey,
    iframeErrors,
    // isStreaming, streamContent removed
    isGeneratingName,
    onHomeClick,
    onHistorySelect,
    onRefreshClick,
    handleGameNameChange,
    handleInstructionChange,
    handleGenerateFromInput,
    handleIframeError,
    fixErrors,
    onConstantChange,
    onSaveGameSettings,
    onSaveCodeEdit,
    onDeviceClick,
    devicePreview,
    onAIStatusChange,
    isGeneratingGame,
    isUpdatingGameArt,
    onSubmitSuggested,
    updatePlanStatus,
    setIsUpdatingGameArt,
    gameArtTriggerReload,
    incrementGameArtTriggerReload,
    currentGameHistoryId,
    gameCreateCompleted,
    loadingMode,
    errorsForModal,
    handleTryToFix,
    handleUndoClick,
    canUndo,
    handleCloseErrorPopup,
    latestUserFacingHistoryId,
    latestUserFacingCode,
    latestUserFacingPrompt,
    latestUserFacingStatus,
  onStreamContext,
  isViewingLatest,
  selectedEntrySnapshot,
  realtimeArtEvent,
}) => {
    const isMobile = useMediaQuery('(max-width: 400px)');

    if (loading) {
        return (
            <Root id="game-edit">
                <LoadingSpinner colorMode='dark' />
            </Root>
        )
    } else {
        return (
            <Root id="game-edit">
                <Helmet>
                    <title>{gameName ? `${gameName}` : 'Game'}</title>
                </Helmet>
                <SharedPanelProvider>
                <GlobalMessage config={config} colorMode="dark" />
                {config.ENABLE_AI_STREAM && (
                    <StreamStateProvider
                        config={config}
                        isActive={true}
                        onStreamContext={onStreamContext}
                    />
                )}
                <GameTopBar
                    config={config}
                    gameId={gameId}
                    gameName={gameName}
                    mode={mode}
                    setMode={setMode}
                    onHomeClick={onHomeClick}
                    onRefreshClick={onRefreshClick}
                    handleGameNameChange={handleGameNameChange}
                    // streamContent and isStreaming removed
                    onDeviceClick={onDeviceClick}
                    devicePreview={devicePreview}
                    incrementGameArtTriggerReload={incrementGameArtTriggerReload}
                    gameCreateCompleted={gameCreateCompleted}
                    latestUserFacingHistoryId={latestUserFacingHistoryId}
                    latestUserFacingCode={latestUserFacingCode}
                    latestUserFacingPrompt={latestUserFacingPrompt}
                    latestUserFacingStatus={latestUserFacingStatus}
                    isGeneratingGame={isGeneratingGame}
                    isUpdatingGameArt={isUpdatingGameArt}
                    currentGameHistoryId={currentGameHistoryId}
                    isViewingLatest={isViewingLatest}
                />
                {/* Progress message: */}
                <GameMessage 
                    config={config} 
                    plan={plan} 
                    gameId={gameId} 
                    // hasStreamContent removed - will be reimplemented with SSE
                    onAIStatusChange={onAIStatusChange}
                    onStreamContext={onStreamContext}
                />
                {/* Suggestion message: */}
                <GameMessage 
                    key={history[selectedHistoryIndex]?.id}
                    gameId={gameId}
                    config={config} 
                    plan={plan} 
                    type="suggestion" 
                    // hasStreamContent removed - will be reimplemented with SSE
                    onAIStatusChange={onAIStatusChange}
                    onSubmitSuggested={onSubmitSuggested} 
                />
                <GameRuntimeError
                    errors={errorsForModal}
                    handleTryToFix={handleTryToFix}
                    handleUndoClick={handleUndoClick}
                    canUndo={canUndo}
                    isGeneratingGame={isGeneratingGame}
                    isUpdatingGameArt={isUpdatingGameArt}
                    handleCloseErrorPopup={handleCloseErrorPopup}
                />
                <Primary>
                    {config.GAME_ASSETS_INTEGRATION.panel && (
                        <GameAssetsPanel 
                            config={config}
                            gameId={gameId} 
                            historyIteration={history.filter(entry => entry.user_facing === true).length} 
                            historyId={currentGameHistoryId}
                            currentGameHistoryId={currentGameHistoryId} 
                            updatePlanStatus={updatePlanStatus}
                            setIsUpdatingGameArt={setIsUpdatingGameArt}
                            isGeneratingGame={isGeneratingGame}
                            isUpdatingGameArt={isUpdatingGameArt}
                            triggerReload={gameArtTriggerReload}
                            mode={mode}
                            isViewingLatest={isViewingLatest ?? true}
                            snapshotGameArt={selectedEntrySnapshot}
                            realtimeArtEvent={realtimeArtEvent}
                        />
                    )}
                    <Inner id="inner">
                        <StageContainer id="stage">
                            {(mode === 'Game' || mode === 'Code') && (
                            <>
                                {(() => {
                                    // console.log('loadingMode: ', loadingMode);
                                    if (loadingMode) {
                                        return (
                                            <PreviewLoading>
                                                <LoadingSpinner colorMode='dark' size={isMobile ? "medium" : "fullscreen"}/>
                                            </PreviewLoading>
                                        )
                                    }
                                    return <GamePreview
                                        config={config}
                                        gameId={gameId}
                                        key={sandpackKey}
                                        mode={mode} 
                                        code={code} 
                                        onError={handleIframeError}
                                        devicePreview={devicePreview}
                                        onSaveCodeEdit={onSaveCodeEdit}
                                    />
                                })()}
                                {/* Legacy inline error alert removed */}
                            </>
                            )}
                            {mode === 'Plan' && (
                                <GamePlan
                                    content={plan}
                                    // streamContent and isStreaming removed
                                />
                            )}
                        </StageContainer>
                        <GameInput 
                            config={config}
                            history={history}
                            onHistorySelect={onHistorySelect}
                            selectedHistoryId={currentGameHistoryId}
                            instruction={instruction}
                            handleInstructionChange={handleInstructionChange}
                            handleGenerateFromInput={handleGenerateFromInput}
                            isGeneratingName={isGeneratingName}
                            isGeneratingGame={isGeneratingGame}
                            isUpdatingGameArt={isUpdatingGameArt}
                        />
                    </Inner>
                    {config.GAME_BALANCE_PANEL && (
                        <GameSettingsPanel 
                            code={code}
                            originalCode={originalCode}
                            onConstantChange={(updatedCode) => {
                                // Pass updated code to parent component for real-time preview
                                if (onConstantChange) {
                                    onConstantChange(updatedCode);
                                }
                            }}
                            onSaveChanges={(updatedCode) => {
                                // Create a new history entry with the updated code
                                if (onSaveGameSettings) {
                                    onSaveGameSettings(updatedCode);
                                }
                            }}
                            mode={mode}
                            historyEntryId={currentGameHistoryId}
                            isGeneratingGame={isGeneratingGame}
                            isUpdatingGameArt={isUpdatingGameArt}
                            isViewingLatest={isViewingLatest}
                            latestUserFacingPrompt={latestUserFacingPrompt}
                        />
                    )}
                </Primary>
                </SharedPanelProvider>
            </Root>
        );
    }
};

export default GameEdit;